// ApiProvider.tsx
import React from 'react'
import { ApolloProvider } from '@apollo/client'
import { useAsync } from 'react-async-hook'
import { getApiUrl } from '@strata-monorepo/utils/_SiteConfig'
import { withRetry } from '@strata-monorepo/shared-ui/utils/retryUtil'
import type { IncenseFactoryInstance } from '@redventures/incense/IncenseFactory'
import { getTokenStrategy } from './strategies/TokenStrategyFactory'
import type { TokenStrategyType } from './strategies/TokenStrategyFactory'

/**
 * Generic interface for ApiProvider that accepts a typed Easy-Peasy store hook.
 *
 * This approach leverages the typed hook exports (TypedStoreState) from each store
 * to provide full type safety while maintaining compatibility with Easy-Peasy's
 * complex type system. Each library can specify its own store type when using
 * the provider.
 *
 * @template StoreHookType - The typed useStoreState hook type from the store
 * @param UseStoreState - The typed Easy-Peasy useStoreState hook from the store
 * @param mode - Token strategy type: 'readOnly' for read-only token, 'session' for session token with Cohesion
 * @param loadingComponent - Optional custom loading component (defaults to null)
 * @param errorComponent - Optional custom error component (defaults to generic error message)
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
interface ApiProviderProps<StoreHookType extends (selector: (state: any) => any) => any> {
  children: React.ReactNode
  getIncense: () => IncenseFactoryInstance | undefined
  UseStoreState: StoreHookType
  mode?: TokenStrategyType
  loadingComponent?: React.ReactNode
  errorComponent?: React.ReactNode
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const ApiProvider = <HookType extends (selector: (state: any) => any) => any>({
  children,
  getIncense,
  UseStoreState,
  mode = 'readOnly',
  loadingComponent = null,
  errorComponent,
}: ApiProviderProps<HookType>): React.ReactElement | null => {
  const incense = getIncense()
  const metricsClient = UseStoreState((state) => state.metrics?.metricsClient)

  const { loading, error } = useAsync(async () => {
    const strategy = getTokenStrategy(mode)

    const { result, retryCount } = await withRetry(
      () => strategy.createToken({ apiUrl: getApiUrl(), metricsClient }),
      {
        maxRetries: 4,
        baseDelay: 500,
        logAttempts: true,
      },
    )

    metricsClient?.increment(strategy.retryMetricName, retryCount, {
      retryCount: retryCount.toString(),
    })

    return result
  }, [mode])

  if (loading) {
    return <>{loadingComponent}</>
  }

  if (error) {
    const strategy = getTokenStrategy(mode)

    metricsClient?.increment(strategy.errorMetricName, 1, {
      errorType: 'retry_exhausted',
      mode: strategy.modeName,
    })

    if (incense) {
      incense(error)
        .captureDetails({
          name: 'ApiProvider.Error',
          message: `Error initializing API in ${strategy.modeName} mode`,
        })
        .sendError()
    }

    if (errorComponent) {
      return <>{errorComponent}</>
    }

    throw error
  }

  const strategy = getTokenStrategy(mode)
  return <ApolloProvider client={strategy.apolloClient}>{children}</ApolloProvider>
}

export default ApiProvider
